import React, { useEffect, useMemo, useRef, useState } from "react";
import { Activity, ChevronRight, Focus, Layers3, Pause, RotateCcw, RotateCw, Search, X } from "lucide-react";
import AnatomyScene from "./AnatomyScene";
import { asset } from "./model-download";
import { EDITIONS, SYSTEMS, defaultVisible, described, edition, edition_, explanation, systemName, term } from "./anatomy";
import { LANGUAGES, direction, fold, say } from "./i18n";
import "./AtlasViewer.css";

// Human Atlas viewer rendered directly inside Doctolik (no iframe). Interface
// adapted from atlas/Human-Atlas/app/page.tsx to the Timeline panel.
const VIEWS = [["three-quarter", "viewThreeQuarter", "¾"], ["front", "viewFront", "F"], ["side", "viewSide", "S"], ["back", "viewBack", "B"]];
const ORGAN_SYSTEMS = ["cardiac", "respiratory", "digestive", "urinary", "endocrine", "reproductive"];
const initialState = (sex) => ({ explode: 0, visible: defaultVisible(sex), selected: [], isolate: false, view: "three-quarter", rotate: false, reset: 0 });
// A saved scene (from the plugin configuration) applies only to the body it was made on,
// since the available systems and structures differ between the two bodies.
const sceneState = (sex, scene) => scene?.sex === sex
	? { ...initialState(sex), visible: scene.visible, view: scene.view, explode: scene.explode, rotate: scene.rotate }
	: initialState(sex);

// A missing model folder on the server comes back as the SPA's HTML page, so
// parse defensively and report a readable error instead of a JSON syntax error.
async function loadManifest(sex, signal) {
	const response = await fetch(asset(edition(sex).manifest), { signal });
	const body = response.ok ? await response.text() : "";
	try {
		return JSON.parse(body);
	} catch {
		throw new Error("Les fichiers 3D de l'atlas sont introuvables sur le serveur (plugins/atlas/models).");
	}
}

export default function AtlasViewer({ initialSex = "male", initialLanguage = "en", initialScene = null, onSceneChange }) {
	const [sex, setSex] = useState(initialSex);
	const [language, setLanguage] = useState(initialLanguage);
	const [atlas, setAtlas] = useState(null);
	const [state, setState] = useState(() => sceneState(initialSex, initialScene));
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState("");
	const [panel, setPanel] = useState(null);
	const [details, setDetails] = useState(false);
	const [query, setQuery] = useState("");
	const [chosen, setChosen] = useState(null);
	const [attempt, setAttempt] = useState(0);
	const pendingFocus = useRef(initialScene?.sex === initialSex ? initialScene : null);
	const reportScene = useRef(onSceneChange);
	reportScene.current = onSceneChange;
	const t = (phrase) => say(language, phrase);
	const name = (text) => term(text, language);
	const rtl = direction(language) === "rtl";
	const source = edition_(sex, language);

	useEffect(() => {
		const abort = new AbortController();
		loadManifest(sex, abort.signal)
			.then((data) => { setError(""); setAtlas(data); })
			.catch((reason) => { if (reason.name !== "AbortError") setError(reason.message); });
		return () => abort.abort();
	}, [sex, attempt]);

	const switchBody = (next) => {
		setSex(next);
		setAtlas(null);
		setProgress(0);
		setError("");
		setChosen(null);
		setDetails(false);
		setPanel(null);
		setQuery("");
		setState(initialState(next));
	};

	const parts = useMemo(() => new Map(atlas?.parts.map((part) => [part.id, part])), [atlas]);
	const counts = useMemo(() => Object.fromEntries(SYSTEMS.map((system) => [system.id, atlas?.parts.filter((part) => part.system === system.id).length ?? 0])), [atlas]);
	const activeSystems = SYSTEMS.filter((system) => counts[system.id] > 0);
	const selectedParts = state.selected.map((id) => parts.get(id)).filter(Boolean);
	const selected = selectedParts[0];
	const selectedSystem = SYSTEMS.find((system) => system.id === selected?.system);
	const visibleCount = atlas?.parts.filter((part) => state.isolate ? state.selected.includes(part.id) : state.visible.includes(part.system) || state.selected.includes(part.id)).length ?? 0;
	const results = useMemo(() => {
		if (!atlas) return [];
		const needle = query.toLowerCase().trim();
		if (!needle) return edition(atlas.sex ?? "male").suggestions.map((suggestion) => atlas.concepts.find((concept) => concept.name.toLowerCase() === suggestion)).filter(Boolean);
		const folded = fold(needle);
		return atlas.concepts
			.filter((concept) => concept.name.toLowerCase().includes(needle) || concept.id.toLowerCase().includes(needle) || fold(term(concept.name, language)).includes(folded))
			.sort((a, b) => a.name.length - b.name.length)
			.slice(0, 80);
	}, [atlas, query, language]);

	const choose = (concept) => {
		setChosen(concept);
		setState((current) => ({ ...current, selected: concept.elements, isolate: false, rotate: false }));
		setDetails(true);
		setPanel(null);
	};
	const choosePart = (id) => {
		const part = parts.get(id);
		if (!part) return;
		setChosen({ id: part.conceptId, name: part.name, elements: [id] });
		setState((current) => ({ ...current, selected: [id], isolate: false, rotate: false }));
		setDetails(true);
		setPanel(null);
	};
	// Restore the configured selection once the saved body's catalogue is loaded.
	useEffect(() => {
		const scene = pendingFocus.current;
		if (!atlas || !scene) return;
		pendingFocus.current = null;
		const concept = scene.focus?.concept && atlas.concepts.find((item) => item.id === scene.focus.concept);
		if (scene.focus?.part && parts.has(scene.focus.part)) choosePart(scene.focus.part);
		else if (concept) choose(concept);
		else return;
		setState((current) => ({ ...current, isolate: scene.isolate }));
	}, [atlas]);

	useEffect(() => {
		// Wait until the saved selection is restored, so it is not reported as cleared.
		if (pendingFocus.current) return;
		reportScene.current?.({
			sex,
			language,
			visible: state.visible,
			view: state.view,
			explode: state.explode,
			rotate: state.rotate,
			isolate: state.isolate,
			focus: chosen ? { concept: chosen.id, part: state.selected.length === 1 ? state.selected[0] : null } : null,
		});
	}, [sex, language, state.visible, state.view, state.explode, state.rotate, state.isolate, state.selected, chosen]);

	const toggleSystem = (id) => {
		setDetails(false);
		setState((current) => ({ ...current, selected: [], isolate: false, visible: current.visible.includes(id) ? current.visible.filter((system) => system !== id) : [...current.visible, id] }));
	};
	const showSystems = (visible) => setState((current) => ({ ...current, selected: [], isolate: false, visible }));
	const reset = () => {
		setState((current) => ({ ...initialState(sex), reset: current.reset + 1 }));
		setChosen(null);
		setDetails(false);
		setPanel(null);
	};
	const togglePanel = (next) => {
		setDetails(false);
		setPanel((current) => current === next ? null : next);
	};
	const clearSelection = () => {
		setState((current) => ({ ...current, selected: [], isolate: false }));
		setDetails(false);
	};

	return <div className="AtlasViewer" dir={direction(language)} lang={language}>
		{atlas && <AnatomyScene atlas={atlas} state={{ ...state, inspectorOpen: details && selectedParts.length > 0 }} onSelect={choosePart} onProgress={(value) => { setProgress(value); if (value === 100) setError(""); }} onError={setError} />}

		<header className="AtlasHeader">
			<div className="AtlasIdentity">
				<strong>{t("title")} <span className="AtlasBadge">3D</span></strong>
				<span>{atlas ? atlas.parts.length.toLocaleString("en") : "—"} {t("pieces")} · {source.dataset}</span>
			</div>
			<div className="AtlasActions">
				<select value={sex} onChange={(event) => switchBody(event.target.value)} aria-label={t("referenceBodyLabel")}>
					{EDITIONS.map((item) => <option key={item.sex} value={item.sex}>{edition_(item.sex, language).label}</option>)}
				</select>
				<button type="button" className={panel === "layers" ? "is-active" : ""} onClick={() => togglePanel("layers")} aria-pressed={panel === "layers"}><Layers3 size={15} /><span>{t("systems")}</span></button>
				<button type="button" className={panel === "search" ? "is-active" : ""} onClick={() => togglePanel("search")} aria-pressed={panel === "search"}><Search size={15} /><span>{t("findStructure")}</span></button>
				<button type="button" onClick={() => setLanguage((current) => current === "en" ? "ar" : "en")} aria-label={t("language")}>{LANGUAGES.find((item) => item.id !== language)?.label}</button>
			</div>
		</header>

		{panel === "layers" && <section className="AtlasPanel AtlasLayers" aria-label={t("anatomicalLayers")}>
			<div className="AtlasPanelHead"><span>{t("systems")}</span><button type="button" onClick={() => setPanel(null)} aria-label={t("closeSystems")}><X size={15} /></button></div>
			<div className="AtlasPresets">
				<button type="button" aria-pressed={activeSystems.every((system) => state.visible.includes(system.id))} onClick={() => showSystems(activeSystems.map((system) => system.id))}>{t("presetAll")}</button>
				<button type="button" aria-pressed={state.visible.length === 1 && state.visible[0] === "skeletal"} onClick={() => showSystems(["skeletal"])}>{t("presetSkeleton")}</button>
				<button type="button" aria-pressed={state.visible.length === ORGAN_SYSTEMS.length && ORGAN_SYSTEMS.every((id) => state.visible.includes(id))} onClick={() => showSystems(ORGAN_SYSTEMS)}>{t("presetOrgans")}</button>
			</div>
			<div className="AtlasSystemList">
				{activeSystems.map((system) => <label key={system.id} className="AtlasSystemRow">
					<input type="checkbox" checked={state.visible.includes(system.id)} onChange={() => toggleSystem(system.id)} />
					<span className="AtlasDot" style={{ background: system.color }} />
					<span className="AtlasSystemName">{systemName(system.id, language)}</span>
					<span className="AtlasCount">{counts[system.id]}</span>
				</label>)}
			</div>
			<div className="AtlasPanelFoot"><span>{visibleCount.toLocaleString("en")} {t("piecesVisible")}</span><button type="button" onClick={() => showSystems([])}>{t("hideAll")}</button></div>
		</section>}

		{panel === "search" && <section className="AtlasPanel AtlasSearch" aria-label={t("findAnatomy")}>
			<div className="AtlasPanelHead"><span>{t("findStructure")}</span><button type="button" onClick={() => setPanel(null)} aria-label={t("closeSearch")}><X size={15} /></button></div>
			<input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("searchPlaceholder")} aria-label={t("searchNamed")} />
			<div className="AtlasResults">
				{results.map((concept) => <button type="button" key={concept.id} onClick={() => choose(concept)}><span>{name(concept.name)}</span><small>{concept.elements.length} {concept.elements.length === 1 ? t("piece") : t("pieces_plural")}</small></button>)}
				{results.length === 0 && <p>{t("noMatches")}</p>}
			</div>
			<p className="AtlasNote">{query ? t("searchHintTyping") : t("searchHintEmpty")}</p>
		</section>}

		<nav className="AtlasViews" aria-label={t("cameraControls")}>
			{VIEWS.map(([view, label, glyph]) => <button type="button" key={view} className={state.view === view ? "is-active" : ""} aria-pressed={state.view === view} disabled={state.explode > 0.8 && view !== "front"} title={t(label)} aria-label={t(label)} onClick={() => setState((current) => ({ ...current, view, reset: current.reset + 1, rotate: false }))}>{glyph}</button>)}
			<button type="button" disabled={state.explode >= 0.4} className={state.rotate ? "is-active" : ""} title={t("autoRotate")} aria-label={state.rotate ? t("pauseRotation") : t("rotateBody")} onClick={() => setState((current) => ({ ...current, rotate: !current.rotate }))}>{state.rotate ? <Pause size={15} /> : <RotateCw size={15} />}</button>
			<button type="button" title={t("reset")} aria-label={t("resetView")} onClick={reset}><RotateCcw size={15} /></button>
		</nav>

		<div className="AtlasDock">
			<label htmlFor="atlas-explode">{t("explode")}</label>
			<input id="atlas-explode" type="range" min={0} max={100} step={1} value={Math.round(state.explode * 100)} onChange={(event) => { const value = Number(event.target.value); setState((current) => ({ ...current, explode: value / 100, view: value > 80 ? "front" : current.view, rotate: false })); }} />
			<output>{Math.round(state.explode * 100)}%</output>
		</div>

		{progress < 100 && !error && <div className="AtlasStatus" role="status"><Activity size={16} /><div><strong>{t("preparing")}</strong><span>{progress}% · {t("loadingBody")} {source.label.toLowerCase()} {t("reference")}</span><div className="AtlasTrack"><i style={{ width: `${progress}%` }} /></div></div></div>}
		{error && <div className="AtlasStatus is-error" role="alert"><p>{error}</p><button type="button" onClick={() => { setError(""); setAtlas(null); setProgress(0); setAttempt((current) => current + 1); }}>{t("reloadViewer")}</button></div>}

		{details && selectedParts.length > 0 && <aside className="AtlasDetail">
			<div className="AtlasDetailHead">
				<span className="AtlasAccent" style={{ background: selectedSystem?.color }} />
				<div>
					<small>{selectedSystem ? systemName(selectedSystem.id, language) : t("anatomy")}</small>
					<h4>{chosen ? name(chosen.name) : ""}</h4>
					{chosen && rtl && name(chosen.name) !== chosen.name && <em>{chosen.name}</em>}
				</div>
				<button type="button" onClick={() => setDetails(false)} aria-label={t("closeSearch")}><X size={15} /></button>
			</div>
			<div className="AtlasDetailBody">
				<p>{chosen && selected ? explanation(chosen.name, selected.system, sex, language) : ""}</p>
				{chosen && !described(chosen.name) && <small className="AtlasNote">{t("systemOverview")}</small>}
				<dl><div><dt>{t("atlasReference")}</dt><dd>{chosen?.id}</dd></div><div><dt>{t("selectedPieces")}</dt><dd>{state.selected.length.toLocaleString("en")}</dd></div></dl>
				{selectedParts.length > 1 && <div className="AtlasMembers"><strong>{t("includedStructures")}</strong>{selectedParts.slice(0, 50).map((part) => <button type="button" key={part.id} onClick={() => choosePart(part.id)}><span>{name(part.name)}</span><ChevronRight size={13} /></button>)}{selectedParts.length > 50 && <small>{t("and")} {selectedParts.length - 50} {t("andMore")}</small>}</div>}
			</div>
			<div className="AtlasDetailActions">
				<button type="button" className={`AtlasPrimary ${state.isolate ? "is-active" : ""}`} onClick={() => setState((current) => ({ ...current, isolate: !current.isolate, explode: 0 }))}><Focus size={15} />{state.isolate ? t("showSurrounding") : t("isolate")}</button>
				<button type="button" onClick={clearSelection}>{t("clearSelection")}</button>
			</div>
		</aside>}

		<footer className="AtlasCredit" title={source.credit}>{source.dataset} · CC BY 4.0</footer>
	</div>;
}
